import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CurrencyContextService } from './currency-context.service';

/**
 * WHICH BOOK AM I LOOKING AT? — the view-level currency, chosen once and applied everywhere.
 *
 * ── Not the same question as the picker at a payment ─────────────────────────────────────────
 * {@see CurrencyChoiceComponent} asks which book a charge is CREATED in; that decision is permanent
 * and belongs to one truck. THIS asks which book the operator is READING, and changes nothing about
 * the data. Picking ZiG does not convert anything — this system holds no exchange rate — it simply
 * shows the ZiG book instead of the dollar one. Parallel books already exist; this chooses one.
 *
 * ── Why it is held here rather than per screen ───────────────────────────────────────────────
 * Tatenda, 2026-10-06: *"when you choose ZIG… everything on the dashboard turns into ZiG. It's the
 * default picker, what you see from that moment is ZiG."* A choice that reset on every navigation
 * would be a filter the operator has to re-apply, not a mode they are in — so it is held once, for
 * the session, and every screen reads the same answer.
 *
 * ── And why it survives a reload but not a sign-out ──────────────────────────────────────────
 * Kept in `localStorage` so a refresh does not silently drop an operator back into dollars while they
 * are reading ZiG figures; cleared by {@see reset} on sign-out, because the next person at that
 * terminal must start from the base rather than inherit a view they did not choose.
 */
@Injectable({ providedIn: 'root' })
export class CurrencyViewService {
  private static readonly STORAGE_KEY = 'mdp_currency_view';

  /** Emits the book in view whenever it changes, so a screen can reload its figures. */
  public readonly changed = new BehaviorSubject<string | null>(null);

  constructor(private context: CurrencyContextService) {}

  /**
   * The book currently in view. The BASE until somebody chooses otherwise — never a remembered
   * non-base book that the system can no longer settle in, which would leave an operator reading a
   * column that is not offered anywhere else.
   */
  get book(): string {
    const stored = this.stored();

    return stored !== null && this.offers(stored) ? stored : this.context.base;
  }

  /** Is the base book in view? Used to decide whether to say so on screen. */
  get isBase(): boolean {
    return this.context.isBase(this.book);
  }

  /**
   * The books on offer — only ones the system can actually settle in. Offering a currency the API
   * would refuse everywhere else is offering a column that can never have anything in it.
   */
  get choices(): Array<string> {
    return this.context.settleable;
  }

  /**
   * Is there a choice to make? While settlement is off there is one book, so the control renders
   * nothing — the same rule every other currency surface follows.
   */
  get asks(): boolean {
    return this.context.settlementEnabled && this.choices.length > 1;
  }

  /** Switch the view. Ignored for a book the system cannot settle in. */
  choose(code: string): void {
    const normalised = (code ?? '').trim().toUpperCase();
    if (!this.offers(normalised)) {
      return;
    }

    try {
      localStorage.setItem(CurrencyViewService.STORAGE_KEY, normalised);
    } catch (e) {
      // A browser with storage blocked still gets the choice for this page; it just will not survive
      // a reload. Losing the preference is better than breaking the dashboard.
    }

    this.changed.next(normalised);
  }

  /** Forget the choice — on sign-out, so the next person starts from the base. */
  reset(): void {
    try {
      localStorage.removeItem(CurrencyViewService.STORAGE_KEY);
    } catch (e) {
      // nothing to clean up
    }

    this.changed.next(null);
  }

  /**
   * Read a per-currency map for the book in view. Returns 0 rather than null for a book the map does
   * not mention: a book with no money in it is a FACT worth showing as zero, not an empty cell the
   * reader has to interpret. Null only when there is no map at all (settlement off), so a caller can
   * fall back to the scalar it has always shown.
   */
  pick(map: Record<string, number> | null | undefined): number | null {
    if (!map) {
      return null;
    }

    return map[this.book] ?? 0;
  }

  private offers(code: string): boolean {
    return this.choices.some((c) => c.trim().toUpperCase() === code.trim().toUpperCase());
  }

  private stored(): string | null {
    try {
      return localStorage.getItem(CurrencyViewService.STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }
}
